import { useRouter } from 'next/router'
import { useCallback, useContext } from 'react'
import { toast } from 'react-toastify'
import { reqRegisterUser } from '@api/api'
import ROUTES from '@constant/Routes'
import { UserContext } from '@context/UserContext'
import useLoadingOverlay from '@hook/loadingOverlay/useLoadingOverlay'
import { RegisterUserDto } from 'types/dto'
import { TRUSTED_DOMAINS, TrustedDomains } from '@constant/SSO'
import { pushToDataLayer } from '@util/gtm'
import { GTM_CUSTOM_TAG } from '@util/gtmTag'
import { reqUserInviteRegistration } from '@api/user-invite'

type Props = { onError?: (_: any) => void; id?: string }

function useRegister({ onError, id }: Props = { onError: () => null }) {
  const router = useRouter()
  const routeQuery = router.query
  const { changeHasUserStatus } = useContext(UserContext)
  const { setLoadingOverlay } = useLoadingOverlay()
  const registerUser = useCallback(
    async (values: RegisterUserDto) => {
      setLoadingOverlay(true)
      try {
        let data
        if (id) {
          data = await reqUserInviteRegistration({ id, ...values })
        } else {
          data = (
            await reqRegisterUser({
              ...values,
              origin: (router.query.origin as string) ?? 'endtherig',
            })
          )?.data
        }
        localStorage.setItem('jwt', data.access_token)
        changeHasUserStatus(true)
        toast.success('You have successfully completed registration.')

        pushToDataLayer({
          event: GTM_CUSTOM_TAG.COMPLETE_REGISTRATION,
          user_Email: values.email,
          timestamp: new Date().getTime(),
        })

        if (
          routeQuery.origin &&
          Object.keys(TRUSTED_DOMAINS).includes(router.query.origin as TrustedDomains)
        ) {
          window.location.href = `${
            TRUSTED_DOMAINS[routeQuery.origin as TrustedDomains]
          }/authenticating?tempToken=${data.provisionalToken}`
        } else if (routeQuery.redirectTo && routeQuery.redirectTo !== '') {
          router.push(routeQuery.redirectTo as string)
        } else {
          router.push(ROUTES.Download)
        }
      } catch (error) {
        onError?.(error)
      }
      setLoadingOverlay(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router, changeHasUserStatus],
  )

  return {
    registerUser,
  }
}

export default useRegister
